import React, { memo, useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from "react-redux";
import ApiService from '../../services/ApiService';
import { authActions } from '../../redux/auth';
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import PreLoader from "../PreLoader";
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

// Validation Schemas
const zelleSchema = yup.object().shape({
    zelle_email: yup.string()
        .transform((value) => (!value ? null : value))
        .email('Enter a valid email address')
        .required('Zelle Email is required'),
    zelle_phone: yup.string()
        .transform((value) => (!value ? null : value))
        .required('Zelle Phone is required')
});

const withdrawalSchema = yup.object().shape({
    amount: yup.number()
        .typeError('Amount must be a number')
        .positive('Amount must be greater than 0')
        .min(10, 'Minimum withdrawal amount is $10.00')
        .required('Amount is required')
});

function WithdrawalManually() {
    const dispatch = useDispatch();
    const { userId, userData } = useSelector(state => state.auth);

    const [isLoading, setIsLoading] = useState(false);

    // Form 1: Zelle Config
    const {
        register: registerZelle,
        handleSubmit: handleSubmitZelle,
        formState: { errors: errorsZelle },
        reset: resetZelle,
        setError: setErrorZelle
    } = useForm({
        resolver: yupResolver(zelleSchema),
        mode: 'all'
    });

    // Form 2: Withdrawal
    const {
        register: registerWithdraw,
        handleSubmit: handleSubmitWithdraw,
        formState: { errors: errorsWithdraw },
        reset: resetWithdraw
    } = useForm({
        resolver: yupResolver(withdrawalSchema),
        mode: 'all'
    });



    const [hasPendingRequest, setHasPendingRequest] = useState(false);

    const getUserDetails = useCallback(async () => {
        try {
            const response = await new ApiService().POST("/user-info", { userid: userId });
            if (response?.data?.user_details?.[0]) {
                const resData = response.data.user_details[0];
                dispatch(authActions.UpdateUserData(resData));
            }
        } catch (error) {
            console.error("Error fetching user details:", error);
        }
    }, [userId, dispatch]);

    const checkPendingStatus = useCallback(async () => {
        try {
            const response = await new ApiService().GET(`/user-withdrawal-manual-history?user_id=${userId}`);
            if (response.data && response.data.status === "1") {
                const isPending = response.data.data.some(row => parseInt(row.status) === 0);
                setHasPendingRequest(isPending);
            }
        } catch (error) {
            console.error("Error checking pending status:", error);
        }
    }, [userId]);

    useEffect(() => {
        getUserDetails();
        checkPendingStatus();
    }, [getUserDetails, checkPendingStatus]);

    // Update Zelle fields when user details load
    useEffect(() => {
        if (userData) {
            resetZelle({
                zelle_email: userData.zelle_email || '',
                zelle_phone: userData.zelle_phone || ''
            });
        }
    }, [userData, resetZelle]);

    const handleZelleSubmit = async (data) => {
        const emailVal = data.zelle_email?.trim();
        const phoneVal = data.zelle_phone?.trim();

        setIsLoading(true);
        try {
            const response = await new ApiService().POST("/update-zelle-details", {
                user_id: userId,
                zelle_email: emailVal,
                zelle_phone: phoneVal
            });
            setIsLoading(false);
            if (response.data && response.data.status === "1") {
                toast.success("Zelle details saved successfully!");
                getUserDetails(); // refresh user data
            } else {
                toast.error(response.data?.message || "Failed to update Zelle details.");
            }
        } catch (error) {
            setIsLoading(false);
            console.error("Error saving Zelle details:", error);
            toast.error("An error occurred while saving details.");
        }
    };

    const handleWithdrawalSubmit = async (data) => {
        const hasZelle = userData?.zelle_email && userData?.zelle_phone;
        if (!hasZelle) {
            toast.error("Please save Zelle Email and Phone details first.");
            return;
        }

        const withdrawAmount = parseFloat(data.amount);
        const earnBalance = parseFloat(userData.earn_amount || 0);
        if (withdrawAmount > earnBalance) {
            toast.error("Insufficient balance.");
            return;
        }

        setIsLoading(true);
        try {
            const response = await new ApiService().POST("/user-withdrawal-manual", {
                user_id: userId,
                amount: withdrawAmount,
                currency: 'USD'
            });
            setIsLoading(false);
            if (response.data && response.data.status === "1") {
                Swal.fire({
                    title: 'Success!',
                    text: 'Manual withdrawal request submitted successfully.',
                    icon: 'success',
                    confirmButtonColor: '#8ABB45'
                });
                resetWithdraw({ amount: '' });
                getUserDetails(); // Refresh balance
                checkPendingStatus(); // Lock form
            } else {
                toast.error(response.data?.message || 'Withdrawal failed.');
            }
        } catch (error) {
            setIsLoading(false);
            console.error("Error submitting manual withdrawal:", error);
            toast.error("An error occurred during withdrawal processing.");
        }
    };

    const hasZelle = userData?.zelle_email || userData?.zelle_phone;

    return (
        <>
            <PreLoader Loader={isLoading} />

            <div className="add-fund">
                <h2>Withdrawable Earned Balance (Zelle)</h2>
                <ul className="a-f-m">
                    <li>${Number(userData.earn_amount || 0).toFixed(2)}</li>
                </ul>

                {/* Zelle Account configuration Form */}
                <div className="card mt-4 p-4 shadow-sm border-0" style={{ background: "rgba(255, 255, 255, 0.08)", backdropFilter: "blur(10px)", borderRadius: "16px", color: "inherit" }}>
                    <h3 className="mb-3 text-success fw-bold">Zelle Account Configuration</h3>
                    <form onSubmit={handleSubmitZelle(handleZelleSubmit)}>
                        <div className="row">
                            <div className="col-md-6 mb-3 text-start">
                                <label className="form-label fw-bold">Zelle Registered Email</label>
                                <input
                                    type="email"
                                    className="form-control"
                                    placeholder="Enter Zelle Email"
                                    {...registerZelle("zelle_email")}
                                    style={{ backgroundColor: '#fff', border: '2px solid #000', color: '#000', borderRadius: "12px" }}
                                />
                                {errorsZelle.zelle_email && <p className="text-danger mt-1">{errorsZelle.zelle_email.message}</p>}
                            </div>
                            <div className="col-md-6 mb-3 text-start">
                                <label className="form-label fw-bold">Zelle Registered Phone</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Enter Zelle Phone"
                                    {...registerZelle("zelle_phone")}
                                    style={{ backgroundColor: '#fff', border: '2px solid #000', color: '#000', borderRadius: "12px" }}
                                />
                                {errorsZelle.zelle_phone && <p className="text-danger mt-1">{errorsZelle.zelle_phone.message}</p>}
                            </div>
                        </div>
                        <div className="text-start">
                            <button type="submit" className="btn btn-theme px-4 py-2 rounded-pill fw-bold text-white" style={{ background: 'linear-gradient(135deg, #8ABB45, #729d33)', border: 'none' }}>
                                Save Zelle Details
                            </button>
                        </div>
                    </form>
                </div>

                {hasZelle ? (
                    hasPendingRequest ? (
                        <div className="text-center p-5 mt-4" style={{ background: "rgba(255, 255, 255, 0.05)", borderRadius: "20px", border: "1px solid rgba(255, 255, 255, 0.1)", backdropFilter: "blur(10px)" }}>
                            <div className="mb-4">
                                <i className="fa fa-info-circle text-info" style={{ fontSize: "3rem" }}></i>
                            </div>
                            <h4 className="fw-bold mb-3">Withdrawal Request Pending</h4>
                            <p className="opacity-75" style={{ fontSize: "1.1rem", lineHeight: "1.6" }}>
                                You currently have a withdrawal request under review. To ensure fast processing, we only permit one pending withdrawal request at a time. Please wait until your current request is resolved.
                            </p>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmitWithdraw(handleWithdrawalSubmit)} className="mt-4 text-start">
                            <div className="mb-3">
                                <label className="form-label fw-bold">Amount to Withdraw ($)</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0.01"
                                    className="form-control form-control-lg"
                                    placeholder="0.00"
                                    {...registerWithdraw("amount")}
                                    style={{ backgroundColor: '#fff', border: '2px solid #000', color: '#000', borderRadius: "12px", fontSize: "1.2rem" }}
                                    required
                                />
                                {errorsWithdraw.amount && <p className="text-danger mt-1">{errorsWithdraw.amount.message}</p>}
                            </div>
                            <div className="text-center mt-3">
                                <button
                                    type="submit"
                                    className="button btn-lg btn-theme full-rounded animated right-icn"
                                >
                                    <span>Submit Withdrawal <i className="glyph-icon flaticon-hearts" aria-hidden="true" /></span>
                                </button>
                            </div>
                        </form>
                    )
                ) : (
                    <div className="text-center p-5 mt-4" style={{ background: "rgba(255, 255, 255, 0.05)", borderRadius: "20px", border: "1px solid rgba(255, 255, 255, 0.1)", backdropFilter: "blur(10px)" }}>
                        <div className="mb-4">
                            <i className="fa fa-exclamation-triangle text-warning" style={{ fontSize: "3rem" }}></i>
                        </div>
                        <h4 className="text-white fw-bold mb-3">Zelle Configuration Required</h4>
                        <p className="opacity-75" style={{ fontSize: "1.1rem", lineHeight: "1.6" }}>
                            To request manual withdrawals, please input and save your Zelle email and Zelle phone number above first.
                        </p>
                    </div>
                )}
            </div>
        </>
    );
}

export default memo(WithdrawalManually);
